import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import alert from '../../alert';
import moment from 'moment-timezone';
import Screen from '../../Screen';
import { Card, StatusCard, DeviceInfoCard } from '../../cards';
import { SquareButton, ButtonRow } from '../../JCButtons';
import DeviceModals from '../DeviceModals';
import { PumpHeroPanel, PumpStatsChart, EventFeed } from '../pumpPal';
import colors from '../../../config/colors';
import routes from '../../../navigation/routes';
import keepConnectApi from '../../../api/keepConnectsApi';
import useApi from '../../../hooks/useApi';

/**
 * PumpPalScreen - Full screen override for Pump Pal (deviceType "6")
 * Completely custom layout with pump-specific UI
 *
 * ESP32 MQTT Fields Reference:
 * - P: power (watts)
 * - V: voltage
 * - A: current (amps)
 * - pS: pump state (0=off, 1=running, 2=calibrating)
 * - pR: is pump running (0/1)
 * - pRT: current run time (seconds)
 * - pAI: current run average current
 * - pPI: current run peak current
 * - pCal: calibration complete (0/1)
 * - pCR: calibration run count
 * - pAvgI: calibrated average current
 * - pAvgT: calibrated average duration
 * - pDry: dry run threshold
 * - pOvl: overload threshold
 * - pTR: total run count
 * - pTT: total runtime (seconds)
 * - pAlt: active alert string
 * - U: uptime (seconds)
 */

// Pump state constants matching ESP32
const PUMP_STATES = {
    OFF: 0,
    RUNNING: 1,
    CALIBRATING: 2,
};

// Map pS numeric value to string for UI
const getPumpStateString = (pS, pR) => {
    if (pS === PUMP_STATES.CALIBRATING) return 'calibrating';
    if (pS === PUMP_STATES.RUNNING || pR === 1) return 'running';
    return 'monitoring';
};

function PumpPalScreen({
    // Data
    data,
    deviceData,
    isConnected,
    isLoading,
    refreshing,

    // Permissions
    auth,
    newSettings5,

    // Handlers
    handleTrigger,
    handleDeregister,
    handleTriggerFirmwareUpdate,
    renameKeepConnect,
    toggleModal,

    // Modal state
    visibleTrigger,
    visibleTrigger2,
    visibleTriggerF,

    // Modal setters
    setVisibleTrigger2,
    setVisibleTriggerF,

    // Refresh
    setUpdateNeeded,
    setUpdateNeededHigherLevel,

    // Navigation
    navigation,
    route,

    // Refs
    renameModalRef,
    newDeviceNameRef,

    // Utilities
    convertTimezone,
    appTimezone,
}) {
    const [selectedMetric, setSelectedMetric] = useState('runs');
    const [selectedPeriod, setSelectedPeriod] = useState(7);
    const [chartData, setChartData] = useState([]);
    const [chartLabels, setChartLabels] = useState([]);
    const [events, setEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);

    // API hooks
    const getPumpPalRunsApi = useApi(keepConnectApi.getPumpPalRuns);
    const getPumpPalEventsApi = useApi(keepConnectApi.getPumpPalEvents);
    const getPumpPalStatsApi = useApi(keepConnectApi.getPumpPalStats);

    const deviceInfo = data?.[0];
    const deviceMac = route.params.item.MAC;
    const realtimeData = deviceInfo?.realtimeData ? JSON.parse(deviceInfo.realtimeData) : {};
    const deviceName = route.params.item.Name;

    // =========================================================================
    // Extract pump-specific data from ESP32 MQTT fields
    // =========================================================================

    // Power metrics (P, V, A)
    const power = parseFloat(deviceData?.P || realtimeData?.P) || 0;
    const voltage = parseFloat(deviceData?.V || realtimeData?.V) || 0;
    const amps = parseFloat(deviceData?.A || realtimeData?.A) || 0;

    // Pump state (pS: 0=off, 1=running, 2=calibrating)
    const pumpStateNum = parseInt(deviceData?.pS ?? realtimeData?.pS) || 0;
    const isPumpRunning = parseInt(deviceData?.pR ?? realtimeData?.pR) === 1;
    const pumpState = getPumpStateString(pumpStateNum, deviceData?.pR ?? realtimeData?.pR);

    // Current run info (only when running)
    const currentRunTime = parseFloat(deviceData?.pRT || realtimeData?.pRT) || 0;
    const currentRunAvgCurrent = parseFloat(deviceData?.pAI || realtimeData?.pAI) || 0;
    const currentRunPeakCurrent = parseFloat(deviceData?.pPI || realtimeData?.pPI) || 0;

    // Calibration status
    // NOTE: Only trust pCal=1 from firmware - do NOT use pCR count as fallback
    // If pCR shows 5+ but pCal isn't 1, that's a firmware bug we want to surface
    const pCalValue = deviceData?.pCal ?? realtimeData?.pCal;
    const calibrationRunCount = parseInt(deviceData?.pCR || realtimeData?.pCR) || 0;
    const isCalibrated = parseInt(pCalValue) === 1;

    // Calibrated values (only valid when pCal=1)
    const avgRunCurrent = parseFloat(deviceData?.pAvgI || realtimeData?.pAvgI) || 0;
    const avgRunDuration = parseFloat(deviceData?.pAvgT || realtimeData?.pAvgT) || 0;
    const dryRunThreshold = parseFloat(deviceData?.pDry || realtimeData?.pDry) || 0;
    const overloadThreshold = parseFloat(deviceData?.pOvl || realtimeData?.pOvl) || 0;

    // Totals
    const totalRunCount = parseInt(deviceData?.pTR || realtimeData?.pTR) || 0;
    const totalRuntime = parseFloat(deviceData?.pTT || realtimeData?.pTT) || 0;

    // Active alert
    const activeAlert = deviceData?.pAlt || realtimeData?.pAlt || null;

    // Uptime
    const uptimeSeconds = parseInt(deviceData?.U || realtimeData?.U) || 0;

    // =========================================================================
    // Calculate average interval between runs per day (for interval metric)
    // =========================================================================
    const loadIntervalData = async (labels) => {
        // Fetch all pump runs for the period
        const runsResponse = await getPumpPalRunsApi.request("\"" + deviceMac + "\"", selectedPeriod, 500, 0);

        if (!runsResponse.data || runsResponse.data.length === 0) {
            setChartData(Array(labels.length).fill(0));
            setStatsLoading(false);
            return;
        }

        // Group runs by date
        const runsByDate = {};
        for (const run of runsResponse.data) {
            const runTime = moment.utc(run.startTime).local();
            const dateKey = runTime.format('MMM D');
            if (!runsByDate[dateKey]) {
                runsByDate[dateKey] = [];
            }
            runsByDate[dateKey].push(runTime.valueOf());
        }

        // Calculate average interval for each date in labels
        const intervalData = labels.map(dateLabel => {
            const runs = runsByDate[dateLabel];
            if (!runs || runs.length < 2) {
                return 0; // Need at least 2 runs to calculate interval
            }

            // Sort runs chronologically
            runs.sort((a, b) => a - b);

            // Calculate intervals between consecutive runs
            let totalInterval = 0;
            for (let i = 1; i < runs.length; i++) {
                totalInterval += (runs[i] - runs[i - 1]) / 1000; // Convert ms to seconds
            }

            // Return average interval in seconds
            return Math.round(totalInterval / (runs.length - 1));
        });

        setChartData(intervalData);
        setStatsLoading(false);
    };

    // =========================================================================
    // Chart data - fetch from PumpPalStats API
    // =========================================================================
    const loadChartData = async () => {
        setStatsLoading(true);
        const response = await getPumpPalStatsApi.request("\"" + deviceMac + "\"", selectedPeriod);

        if (response.data && response.data.length > 0) {
            // Build arrays for labels and data from API response
            const labels = [];
            const runCounts = [];
            const runtimeData = [];

            // API returns data ordered by date DESC, so reverse for chronological order
            const sortedData = [...response.data].reverse();

            for (const row of sortedData) {
                labels.push(moment(row.date).format('MMM D'));
                runCounts.push(Number(row.runCount) || 0);
                runtimeData.push(Math.round((Number(row.totalRuntimeSec) || 0) / 60)); // Convert to minutes
            }

            setChartLabels(labels);

            // Set chart data based on selected metric
            if (selectedMetric === 'runs') {
                setChartData(runCounts);
            } else if (selectedMetric === 'runtime') {
                setChartData(runtimeData);
            } else if (selectedMetric === 'interval') {
                // Calculate intervals client-side from PumpRuns data
                await loadIntervalData(labels);
            } else {
                setChartData(runCounts);
            }
        } else {
            // No data - show empty chart with date labels
            const labels = [];
            for (let i = Math.min(selectedPeriod, 7) - 1; i >= 0; i--) {
                labels.push(moment().subtract(i, 'days').format('MMM D'));
            }
            setChartLabels(labels);
            setChartData(Array(labels.length).fill(0));
        }
        setStatsLoading(false);
    };

    useEffect(() => {
        loadChartData();
    }, [selectedPeriod, selectedMetric]);

    // =========================================================================
    // Events - fetch from PumpPalRuns and PumpPalEvents APIs
    // =========================================================================
    const loadEvents = async () => {
        setEventsLoading(true);
        const eventList = [];

        // Add active alert at the top if present (from realtime MQTT data)
        if (activeAlert && activeAlert !== 'none') {
            eventList.push({
                id: 'active-alert',
                type: mapAlertType(activeAlert),
                timestamp: 'now',
                isAlert: true,
                sortTime: Date.now(), // For sorting - active alert always first
            });
        }

        // Fetch pump runs (pump_finished events)
        // NOTE: Alerts are shown from Events table only - PumpRuns always show as pump_finished
        // to avoid duplicate alert entries (same alert appears in both tables)
        const runsResponse = await getPumpPalRunsApi.request("\"" + deviceMac + "\"", 7, 20, 0);
        console.log("PumpPalScreen: Loaded pump runs:", runsResponse.data);
        if (runsResponse.data && runsResponse.data.length > 0) {
            for (const run of runsResponse.data) {
                // Server stores times in UTC - parse as UTC then convert to local
                const runTime = moment.utc(run.startTime).local();
                const timestamp = runTime.fromNow();
                eventList.push({
                    id: `run-${run.id}`,
                    type: 'pump_finished', // Always show as finished - alerts come from Events table
                    timestamp: timestamp,
                    duration: Math.round(Number(run.durationSec) || 0),
                    power: Math.round(Number(run.avgPower) || 0),
                    current: Number(run.avgCurrent)?.toFixed(2),
                    isAlert: false, // Never mark as alert from PumpRuns
                    sortTime: runTime.valueOf(), // For sorting
                });
            }
        }

        // Fetch system events (Events + Resets)
        const eventsResponse = await getPumpPalEventsApi.request("\"" + deviceMac + "\"", 7, 20);
        console.log("PumpPalScreen: Loaded system events:", eventsResponse.data);
        if (eventsResponse.data && eventsResponse.data.length > 0) {
            for (const evt of eventsResponse.data) {
                // Server stores times in UTC - parse as UTC then convert to local
                const evtTime = moment.utc(evt.time).local();
                const timestamp = evtTime.fromNow();
                if (evt.source === 'event') {
                    // Detection event from Events table
                    eventList.push({
                        id: `evt-${evt.time}`,
                        type: mapParamToEventType(evt.param, evt.direction),
                        timestamp: timestamp,
                        isAlert: true,
                        sortTime: evtTime.valueOf(),
                    });
                } else if (evt.source === 'reset') {
                    // System event from Resets table (Power On, Online, Offline)
                    eventList.push({
                        id: `rst-${evt.time}`,
                        type: mapReasonToEventType(evt.reason),
                        timestamp: timestamp,
                        isAlert: false,
                        sortTime: evtTime.valueOf(),
                    });
                }
            }
        }

        // Sort all events by timestamp (most recent first)
        eventList.sort((a, b) => b.sortTime - a.sortTime);

        setEvents(eventList);
        console.log("PumpPalScreen: Loaded events:", eventList);
        setEventsLoading(false);
    };

    useEffect(() => {
        loadEvents();
    }, [activeAlert]);

    // Map ESP32 alert strings to our event types
    const mapAlertType = (alertString) => {
        if (!alertString) return 'pump_finished';
        const alertLower = alertString.toLowerCase();
        if (alertLower.includes('dry')) return 'dry_run';
        if (alertLower.includes('overload')) return 'high_power';
        if (alertLower.includes('excessive') || alertLower.includes('long')) return 'excessive_run';
        if (alertLower.includes('short')) return 'short_cycling';
        if (alertLower.includes('expected')) return 'expected_run';
        return 'unusual_power';
    };

    // Map alertType code from PumpRuns table to event type
    // NOTE: alertType comes from API as STRING, must parse to int
    const mapAlertTypeFromCode = (alertType) => {
        const code = parseInt(alertType) || 0;
        switch (code) {
            case 1: return 'dry_run';
            case 2: return 'high_power';
            case 3: return 'excessive_run';
            case 4: return 'short_cycling';
            case 5: return 'unusual_power'; // power_loss
            case 6: return 'excessive_run'; // stuck_on
            default: return 'pump_finished';
        }
    };

    // Map Events table param to event type
    // NOTE: direction comes from API as STRING, must parse to int
    const mapParamToEventType = (param, direction) => {
        if (!param) return 'unusual_power';
        const paramLower = param.toLowerCase();
        const dir = parseInt(direction) || 0; // 0 = below threshold, 1 = above threshold
        if (paramLower.includes('palt')) return 'unusual_power';
        if (paramLower.includes('psc')) return 'short_cycling'; // Short cycling count
        if (paramLower.includes('pavga') || paramLower.includes('current')) {
            return dir === 0 ? 'dry_run' : 'high_power'; // below = dry run, above = overload
        }
        if (paramLower.includes('prt') || paramLower.includes('runtime')) return 'excessive_run';
        return 'unusual_power';
    };

    // Map Resets table reason to event type
    // NOTE: Resets are DEVICE system events (boot, online, offline), NOT pump cycle events
    const mapReasonToEventType = (reason) => {
        if (!reason) return 'device_event';
        const reasonLower = reason.toLowerCase();
        if (reasonLower.includes('power on') || reasonLower.includes('boot')) return 'device_boot';
        if (reasonLower.includes('offline')) return 'device_offline';
        if (reasonLower.includes('online')) return 'device_online';
        return 'device_event';
    };

    const handleViewAllEvents = () => {
        navigation.navigate(routes.PUMPPALEVENTS, {
            item: route.params.item,
        });
    };

    const handleClearAlert = () => {
        alert(
            'Clear Alert',
            'Are you sure you want to clear the active alert?',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Clear',
                    onPress: () => handleTrigger('pumpClearAlert'),
                },
            ]
        );
    };

    const handleResetCalibration = () => {
        alert(
            'Reset Calibration',
            'This will clear all learned pump behavior and restart the calibration process. Are you sure?',
            [
                { text: 'Cancel', style: 'cancel' },
                {
                    text: 'Reset',
                    style: 'destructive',
                    onPress: () => handleTrigger('pumpResetCal'),
                },
            ]
        );
    };

    return (
        <Screen isLoading={refreshing} setUpdateNeeded={setUpdateNeeded}>
            {/* Status Card */}
            <StatusCard
                isConnected={isConnected}
                deviceName={deviceName}
                onRenamePress={() => renameModalRef.current?.openModal()}
            />

            {/* Hero Panel with Power Display */}
            <PumpHeroPanel
                deviceName={deviceName}
                power={power}
                amps={amps}
                voltage={voltage}
                pumpState={pumpState}
                uptimeSeconds={uptimeSeconds}
                currentRunTime={currentRunTime}
                isCalibrated={isCalibrated}
                calibrationRunCount={calibrationRunCount}
                totalRunCount={totalRunCount}
            />

            {/* Calibration Info Card - show when calibrating */}
            {!isCalibrated && (
                <Card style={styles.calibrationCard}>
                    <Text style={styles.calibrationTitle}>Calibrating...</Text>
                    <Text style={styles.calibrationText}>
                        Learning pump behavior: {calibrationRunCount}/5 runs
                    </Text>
                    <View style={styles.calibrationProgress}>
                        <View style={[styles.calibrationBar, { width: `${(calibrationRunCount / 5) * 100}%` }]} />
                    </View>
                </Card>
            )}

            {/* Alert Banner - show active alert (not when 'none') */}
            {activeAlert && activeAlert !== 'none' && (
                <Card style={styles.alertCard}>
                    <View style={styles.alertHeader}>
                        <View style={styles.alertTextContainer}>
                            <Text style={styles.alertTitle}>Active Alert</Text>
                            <Text style={styles.alertText}>{activeAlert}</Text>
                        </View>
                        <SquareButton
                            title="Clear"
                            onPress={handleClearAlert}
                            icon="times-circle"
                            color="#dc3545"
                            small
                        />
                    </View>
                </Card>
            )}

            {/* Stats Chart */}
            <PumpStatsChart
                data={chartData}
                labels={chartLabels}
                selectedMetric={selectedMetric}
                selectedPeriod={selectedPeriod}
                onMetricChange={setSelectedMetric}
                onPeriodChange={setSelectedPeriod}
                previousPeriodChange={null}
                totalRunCount={totalRunCount}
                totalRuntime={totalRuntime}
                avgRunDuration={avgRunDuration}
            />

            {/* Event Feed */}
            <EventFeed
                events={events}
                maxHeight={250}
                onViewAllPress={handleViewAllEvents}
            />

            {/* Pump Stats Card - show calibrated values */}
            {isCalibrated && (
                <Card style={styles.statsCard}>
                    <Text style={styles.sectionTitle}>Pump Statistics</Text>
                    <View style={styles.statsRow}>
                        <StatItem label="Total Runs" value={totalRunCount} />
                        <StatItem label="Total Runtime" value={formatDuration(totalRuntime)} />
                    </View>
                    <View style={styles.statsRow}>
                        <StatItem label="Avg Duration" value={`${avgRunDuration.toFixed(1)}s`} />
                        <StatItem label="Avg Current" value={`${avgRunCurrent.toFixed(2)}A`} />
                    </View>
                    <View style={styles.statsRow}>
                        <StatItem label="Dry Threshold" value={`${dryRunThreshold.toFixed(2)}A`} />
                        <StatItem label="Overload Threshold" value={`${overloadThreshold.toFixed(2)}A`} />
                    </View>
                </Card>
            )}

            {/* Device Info Card */}
            <DeviceInfoCard
                webId={deviceInfo?.id}
                ipAddress={deviceInfo?.IPaddress}
                macAddress={deviceInfo?.macAddress}
                lastConnected={`${convertTimezone(deviceInfo?.lastConnectTime)} (${moment().tz(appTimezone).format('z')})`}
            />

            {/* Actions */}
            <Card style={styles.actionsCard}>
                <Text style={styles.sectionTitle}>Device Management</Text>
                <ButtonRow>
                    <SquareButton
                        title="View/Change Settings"
                        onPress={() => navigation.navigate(routes.CHANGESETTINGSPUMPPAL, { item: route.params.item, changedDevices: setUpdateNeededHigherLevel })}
                        icon="cogs"
                        color={colors.primary}
                        disabled={auth.userPermissions.role !== 'admin'}
                    />
                    <SquareButton
                        title="View Event History"
                        onPress={handleViewAllEvents}
                        icon="history"
                        color={colors.primary}
                    />
                    <SquareButton
                        title="More Device Details"
                        onPress={() => navigation.navigate(routes.PRESENTSETTINGS, { item: route.params.item })}
                        icon="search-plus"
                        color={colors.primary}
                    />
                </ButtonRow>
                {isCalibrated && (
                    <ButtonRow>
                        <SquareButton
                            title="Reset Calibration"
                            onPress={handleResetCalibration}
                            icon="refresh"
                            color="#ffc107"
                            disabled={auth.userPermissions.role !== 'admin'}
                        />
                    </ButtonRow>
                )}
                {newSettings5 && (
                    <ButtonRow>
                        <SquareButton
                            title="Update Firmware"
                            onPress={() => setVisibleTriggerF?.(true)}
                            icon="cloud-upload"
                            color={colors.primary}
                        />
                        <SquareButton
                            title="Deregister Device"
                            onPress={() => setVisibleTrigger2(true)}
                            icon="trash"
                            color={colors.primary}
                            disabled={auth.userPermissions.role !== 'admin'}
                        />
                    </ButtonRow>
                )}
            </Card>

            {/* Refresh link for non-iOS */}
            {Platform.OS !== 'ios' && (
                <Text style={styles.refreshLink} onPress={() => setUpdateNeeded(true)}>
                    Refresh
                </Text>
            )}

            {/* Shared Modals */}
            <DeviceModals
                deviceName={deviceName}
                visibleTrigger={visibleTrigger}
                handleTrigger={handleTrigger}
                visibleTriggerF={visibleTriggerF}
                handleTriggerFirmwareUpdate={handleTriggerFirmwareUpdate}
                visibleTrigger2={visibleTrigger2}
                handleDeregister={handleDeregister}
                renameModalRef={renameModalRef}
                newDeviceNameRef={newDeviceNameRef}
                renameKeepConnect={renameKeepConnect}
                showVPN={false}
            />
        </Screen>
    );
}

// Helper component for stats
function StatItem({ label, value }) {
    return (
        <View style={styles.statItem}>
            <Text style={styles.statValue}>{value}</Text>
            <Text style={styles.statLabel}>{label}</Text>
        </View>
    );
}

// Helper function to format duration
function formatDuration(seconds) {
    if (!seconds || seconds < 60) return `${Math.round(seconds || 0)}s`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
}

const styles = StyleSheet.create({
    actionsCard: {
        paddingVertical: 12,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: colors.dark,
        marginBottom: 8,
        textAlign: 'center',
    },
    refreshLink: {
        color: colors.primary,
        alignSelf: 'center',
        marginTop: 16,
        marginBottom: 8,
    },
    calibrationCard: {
        backgroundColor: '#fff3cd',
        borderLeftWidth: 4,
        borderLeftColor: '#ffc107',
    },
    calibrationTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#856404',
        marginBottom: 4,
    },
    calibrationText: {
        fontSize: 14,
        color: '#856404',
        marginBottom: 8,
    },
    calibrationProgress: {
        height: 8,
        backgroundColor: '#ffe8a1',
        borderRadius: 4,
        overflow: 'hidden',
    },
    calibrationBar: {
        height: '100%',
        backgroundColor: '#ffc107',
    },
    alertCard: {
        backgroundColor: '#f8d7da',
        borderLeftWidth: 4,
        borderLeftColor: '#dc3545',
    },
    alertHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    alertTextContainer: {
        flex: 1,
        marginRight: 12,
    },
    alertTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#721c24',
        marginBottom: 4,
    },
    alertText: {
        fontSize: 14,
        color: '#721c24',
    },
    statsCard: {
        paddingVertical: 12,
    },
    statsRow: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginVertical: 8,
    },
    statItem: {
        alignItems: 'center',
        flex: 1,
    },
    statValue: {
        fontSize: 18,
        fontWeight: 'bold',
        color: colors.primary,
    },
    statLabel: {
        fontSize: 12,
        color: colors.medium,
        marginTop: 2,
    },
});

export default PumpPalScreen;
